import React, { useContext, useEffect, useState } from 'react';

import { core_apimessages_ShippingLabelPackage } from '@reverbdotcom/commons/src/gql/graphql';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { gql } from '@reverbdotcom/commons/src/gql';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { useUser, useExpEnabled, useEppoExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';
import eppoFlags from '@reverbdotcom/commons/src/eppoFlags';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';

import { SellFormContext } from '../../SellFormContext';
import { buildPackageEntry } from '../../../../discovery/shipping_labels/shipping_label_package';
import CarrierCalculatedPackageForm from './CarrierCalculatedPackageForm';
import { carrierCalculatedPackageValidator } from './CarrierCalculatedPackageValidator';
import ShippingReverbPackageDataToggle from './ShippingReverbPackageDataToggle';
import { SelectedCarrier } from './SelectedCarrier';
import { RCLoadingBars } from '@reverbdotcom/cadence/components';
import { EppoSubjectTypes } from '@reverbdotcom/commons/src/eppo/eppoExperimentHelpers';

interface IProps {
  packageEntry: core_apimessages_ShippingLabelPackage;
  setPackageEntry: (core_apimessages_ShippingLabelPackage) => void;
  setIsInCarrierCalculatedMode: (boolean) => void;
  disabled?: boolean;
}

export default function CarrierCalculatedPackageFormContainer({
  packageEntry,
  setPackageEntry,
  setIsInCarrierCalculatedMode,
  disabled = false,
}: IProps) {
  const user = useUser();
  const { currentCanonicalProductId, listingState } = useContext(SellFormContext);
  const shippingDefaultsEnabled = useEppoExpEnabled({
    flagKey: eppoFlags.SELL_FORM_SHIPPING_DEFAULTS_UI,
    subjectKey: user.id,
    subjectType: EppoSubjectTypes.USER_ID,
  });

  const inSuggestionsExperiment = useExpEnabled(experiments.PACKAGE_SIZE_SUGGESTIONS);
  const upsEnabled = useExpEnabled(experiments.UPS_CALCULATED_SHIPPING);
  const [useReverbPackageData, setUseReverbPackageData] = useState(shippingDefaultsEnabled);
  const [originalPackageEntry, setOriginalPackageEntry] = useState(null);

  // save original suggestion dimensions, so we can reset if the user toggles on the reverb package data switch
  useEffect(() => {
    if (originalPackageEntry === null && packageEntry.length.value !== '') {
      setOriginalPackageEntry(packageEntry);
    }
  }, [packageEntry]);

  function onPackageSuggestionResponse(respData) {
    const suggestionData = respData?.canonicalproduct?.packageSizeSuggestion;
    const validSuggestionData = !!suggestionData && !carrierCalculatedPackageValidator({
      packageEntry: suggestionData,
      upsEnabled,
    })?.errorTranslationKey;
    const currentPackageEntryIsBlank = ['length', 'width', 'height', 'weight'].every((dimensionKey) => !packageEntry[dimensionKey].value);
    const suggestionDiffersFromCurrentEntry = validSuggestionData && ['length', 'width', 'height', 'weight'].some((dimensionKey) => {
      return Number(suggestionData[dimensionKey]?.value) !== Number(packageEntry[dimensionKey]?.value) ||
        suggestionData[dimensionKey]?.unit !== packageEntry[dimensionKey]?.unit;
    });

    const shouldUseSuggestion = validSuggestionData &&
      (currentPackageEntryIsBlank || packageEntry.reverbSuggested) &&
      suggestionDiffersFromCurrentEntry;

    if (shouldUseSuggestion) {
      trackEvent({
        eventName: MParticleEventName.ComponentView,
        componentName: 'PackageSizeSuggestionAlert',
        experiments: formatExperimentsForEventAttributes(user, [experiments.PACKAGE_SIZE_SUGGESTIONS]),
      });

      if (inSuggestionsExperiment) {
        setPackageEntry(buildPackageEntry(suggestionData, undefined, true));
      }
    }
  }

  const { loading, data: packageSuggestionData } = useQuery(
    FETCH_PACKAGE_SIZE_SUGGESTION,
    {
      skip: !currentCanonicalProductId || listingState !== 'draft',
      variables: { canonicalProductId: currentCanonicalProductId },
      onCompleted: onPackageSuggestionResponse,
    },
  );

  if (shippingDefaultsEnabled && loading) {
    return (<div className="shipping-radio-label__inner__form">
      <div className="reverb-package-data-toggle__loading_container"><RCLoadingBars /></div>
    </div>);
  }

  if (packageSuggestionData?.canonicalproduct?.packageSizeSuggestion && shippingDefaultsEnabled) {
    return (
      <div className="shipping-radio-label__inner__form">
        <>
          <ShippingReverbPackageDataToggle packageEntry={originalPackageEntry} checked={useReverbPackageData}
            onChange={() => {
              setPackageEntry(originalPackageEntry);
              setUseReverbPackageData(!useReverbPackageData);
            }} />
          {!useReverbPackageData &&
            <div className="mt-8">
              <CarrierCalculatedPackageForm
                packageEntry={packageEntry}
                setPackageEntry={setPackageEntry}
                setIsInCarrierCalculatedMode={setIsInCarrierCalculatedMode}
                disabled={disabled}
              />
            </div>}
          {
            upsEnabled && !!packageEntry.allowedCarriers?.length &&
            <div className="mt-6">
              <SelectedCarrier
                carrier={packageEntry.allowedCarriers[0]}
              />
            </div>
          }
        </>
      </div>
    );
  }

  return (
    <div className={shippingDefaultsEnabled ? 'shipping-radio-label__inner__form mt-8' : ''}>
      <CarrierCalculatedPackageForm
        packageEntry={packageEntry}
        setPackageEntry={setPackageEntry}
        setIsInCarrierCalculatedMode={setIsInCarrierCalculatedMode}
        disabled={disabled || loading}
      />
      {
        upsEnabled && shippingDefaultsEnabled && !!packageEntry.allowedCarriers?.length &&
        <div className="mt-6">
          <SelectedCarrier
            carrier={packageEntry.allowedCarriers[0]}
          />
        </div>
      }
    </div>
  );
}
export const FETCH_PACKAGE_SIZE_SUGGESTION = gql(`
  query Core_SellForm_FetchPackageSizeSuggestion($canonicalProductId: String!) {
    canonicalproduct(input: { id: $canonicalProductId }) {
      _id
      packageSizeSuggestion {
        _id
        length {
          unit
          value
        }
        width {
          unit
          value
        }
        height {
          unit
          value
        }
        weight {
          unit
          value
        }
      }
    }
  },
`);
